import React, { FC, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import PoorVisionTerminalHeader from '../PoorVisionTerminalHeader/PoorVisionTerminalHeader';
import classes from './PoorVisionMode.module.scss';
import { TerminalMode } from '../../../../../pagesMain/TerminalPage/TerminalPage';
import { languageType } from '../../Normal/NormalMode/NormalMode';
import { ButtonsEditTheme } from '../ButtonsEditTheme/ButtonsEditTheme';
import { ThemeColors } from '../../../../../types/enums';
import TerminalMisScanModal from '../../../Modals/TerminalMisScanModal/TerminalMisScanModal';
import TerminalExtraDataModal from '../../../Modals/TerminalExtraDataModal/TerminalExtraDataModal';
import { OrderUnitsType, TopicsType } from '../../../../../types/Terminal/TerminalTypes';
import { useGetTicket } from '../../../../../hooks/TerminalServices/useGetTicket';
import { ReactComponent as ArrowBack } from '../../../../../assets/icons/arrowBack.svg';
import TerminalModal from '../../../Modals/TerminalModal/TerminalModal';
import { useTerminalAutoReturnHome } from '../../../../../hooks/TerminalServices/useTerminalAutoReturnHome';
import { useTerminalScreensaver } from '../../../../../hooks/TerminalServices/useTerminalScreensaver';
import { TerminalScreensaver } from '../../../TerminalScreensaver/TerminalScreensaver';
import { useMisTerminalSession } from '../../../../../hooks/TerminalServices/useMisTerminalSession';
import {
  TerminalAppearanceResponse,
} from '../../../../../types/Admin/TerminalAppearanceTypes';
import {
  checkIsCategory,
  getServiceTopicId,
  getExtraInputTopic,
  getTicketName,
  isPrivilegeTile,
  TerminalTicketItem,
  transformOrderUnitsToArr
} from '../../Normal/NormalMode/terminalModeShared';

type PoorVisionModePropsType = {
  setMode: (mode: TerminalMode) => void;
  orderUnitsData: OrderUnitsType;
  tickerText?: string;
  autoReturnHomeIdleSeconds?: number;
  appearance?: TerminalAppearanceResponse;
  isPreviewMode?: boolean;
};

const PoorVisionMode: FC<PoorVisionModePropsType> = ({
  setMode,
  orderUnitsData,
  autoReturnHomeIdleSeconds = 0,
  appearance,
  isPreviewMode = false,
}) => {
  const [themeColor, setThemeColor] = useState<string>(ThemeColors.Blue);
  const [language, setLanguage] = useState<languageType>('ru');

  const [currentSectionsServices, setCurrentSectionsServices] = useState<
    TerminalTicketItem[]
  >([]);
  const [history, setHistory] = useState<{ [key: string]: TerminalTicketItem[] }>({});
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [activeModal, setActiveModal] = useState(false);
  const [activeTicketId, setActiveTicketId] = useState<number>();
  const [extraInputTarget, setExtraInputTarget] = useState<TopicsType | null>(null);
  const [pendingExtraData, setPendingExtraData] = useState<string | undefined>();

  const {
    scanTarget,
    scanError,
    isConfirming,
    confirmation,
    privilegeBanner,
    allowsDigitalCode,
    allowsBarcode,
    localTest,
    openScan,
    closeScan,
    submitScan,
    clearConfirmation
  } = useMisTerminalSession(language, orderUnitsData?.misConfig);

  const { getTicket, isLoadingGetTicket } = useGetTicket(
    () => {
      clearConfirmation();
      setPendingExtraData(undefined);
      setExtraInputTarget(null);
      if (currentIndex > 0) {
        setCurrentIndex(0);
        setCurrentSectionsServices(history[0]);
      }
    },
    orderUnitsData?.terminalIp
  );

  useEffect(() => {
    if (isLoadingGetTicket) {
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [isLoadingGetTicket]);

  const bgThemeColor = classNames({
    [classes.bgBlue]: themeColor === ThemeColors.Blue,
    [classes.bgWhite]: themeColor === ThemeColors.White,
    [classes.bgBlack]: themeColor === ThemeColors.Black
  });

  useEffect(() => {
    if (orderUnitsData) {
      setCurrentSectionsServices(transformOrderUnitsToArr(orderUnitsData));

      if (currentIndex > 0) {
        setCurrentIndex(0);
      }
    }
  }, [orderUnitsData]);

  const requestTicket = (
    topicId: number,
    extraData?: string,
    confirmationId?: string
  ) => {
    setActiveTicketId(topicId);
    getTicket({
      topicId,
      confirmationId: confirmationId ?? confirmation?.confirmationId,
      extraData
    });
  };

  const handleCategoryClick = (item: TerminalTicketItem) => {
    if (isPrivilegeTile(item)) {
      openScan({
        privilegeTypeId: item.privilegeTypeId,
        misCode: item.misCode,
        privilegeKind: item.privilegeKind,
        name: getTicketName(item, language),
        scanCard: item.scanCard
      });
      return;
    }
    if (checkIsCategory(item)) {
      setHistory((prev) => {
        const newHistory = { ...prev };
        newHistory[currentIndex] = currentSectionsServices;
        return newHistory;
      });
      setCurrentSectionsServices([
        ...(item.topics || []),
        ...(item.subCategories || [])
      ]);
      setCurrentIndex((prev) => prev + 1);
    } else {
      const topicId = getServiceTopicId(item);
      if (topicId == null) return;
      setPendingExtraData(undefined);
      const extraTopic = getExtraInputTopic(item);
      if (extraTopic) {
        setExtraInputTarget(extraTopic);
        return;
      }
      if (
        !confirmation &&
        orderUnitsData?.misConfig?.benefitsTerminalMode === 'SCAN_ON_SERVICE' &&
        orderUnitsData?.misConfig?.benefitsEffective
      ) {
        openScan({
          privilegeKind: 'BENEFIT',
          name: getTicketName(item, language),
          scanCard: true,
          scanOnService: true,
          topicId
        });
        return;
      }
      requestTicket(topicId);
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setCurrentSectionsServices(history[currentIndex - 1]);
    }
  };

  const resetSessionToHome = useCallback(() => {
    closeScan();
    clearConfirmation();
    setExtraInputTarget(null);
    setPendingExtraData(undefined);
    setActiveModal(false);
    setActiveTicketId(undefined);
    if (orderUnitsData) {
      setCurrentIndex(0);
      setHistory({});
      setCurrentSectionsServices(transformOrderUnitsToArr(orderUnitsData));
    }
  }, [orderUnitsData, closeScan, clearConfirmation]);

  const returnToHome = useCallback(() => {
    resetSessionToHome();
    setMode(TerminalMode.Normal);
  }, [resetSessionToHome, setMode]);

  const screensaverMedia = appearance?.screensaverMedia ?? [];
  const screensaverIdleSeconds =
    appearance?.settings?.screensaverIdleSeconds ?? 120;
  const screensaverImageSeconds =
    appearance?.settings?.screensaverImageSeconds ?? 8;
  const { isScreensaverActive, dismissScreensaver } = useTerminalScreensaver({
    idleSeconds: screensaverIdleSeconds,
    hasMedia: screensaverMedia.length > 0,
    enabled: !isPreviewMode,
  });

  useTerminalAutoReturnHome({
    idleSeconds: autoReturnHomeIdleSeconds,
    isOnHomePage: false,
    onReturnHome: returnToHome,
    enabled: !isPreviewMode && !scanTarget && !extraInputTarget && !isScreensaverActive,
  });

  useEffect(() => {
    if (!isScreensaverActive) return;
    resetSessionToHome();
  }, [isScreensaverActive, resetSessionToHome]);

  const handleClickTheme = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    const color = e.currentTarget.dataset.color;
    setThemeColor(color);
  };
  return (
    <div className={bgThemeColor}>
      <TerminalModal activeModal={activeModal} setActiveModal={setActiveModal} />
      <div className={classes.poorVisionContainer}>
        <div className={classes.headerContainer}>
          <PoorVisionTerminalHeader
            language={language}
            setLanguage={setLanguage}
            setMode={setMode}
            themeColor={themeColor}
          />
        </div>
        <main className={classes.ticketsContainer}>
          <div
            className={classNames(classes.choiceServiceContainer, {
              [classes.withoutArr]: currentIndex !== 0
            })}>
            {currentIndex !== 0 && (
              <button onClick={handleBack} className={classNames(classes.buttonBack)}>
                <ArrowBack
                  className={classNames(classes.backImg, {
                    [classes.blackArrow]: themeColor === ThemeColors.Black
                  })}
                />
              </button>
            )}
            <span
              className={classNames(classes.title, {
                [classes.whiteTitle]: themeColor === ThemeColors.Black
              })}>
              ВЫБЕРИТЕ УСЛУГУ
            </span>
            <ButtonsEditTheme handleClickTheme={handleClickTheme} theme={themeColor} />
          </div>
          {privilegeBanner && (
            <div className={classes.privilegeBanner}>
              <span>{privilegeBanner}</span>
              <button
                type="button"
                className={classes.privilegeBannerClose}
                onClick={clearConfirmation}
                aria-label="Закрыть"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 6 L18 18 M18 6 L6 18" />
                </svg>
              </button>
            </div>
          )}
          <div className={classes.ticketList}>
            {currentSectionsServices.length > 0 &&
              currentSectionsServices.map((ticket: TerminalTicketItem) => (
                <button
                  className={classNames(classes.ticket, {
                    [classes.noBorders]: themeColor === ThemeColors.Black,
                    [classes.smallText]:
                      (language === 'by' && ticket.nameBe.length > 28) ||
                      (language === 'ru' && ticket.nameRu.length > 28) ||
                      (language === 'en' && ticket.nameEn.length > 28)
                  })}
                  key={ticket.id}
                  onClick={() => handleCategoryClick(ticket)}
                  disabled={
                    (!ticket.isActiveNow && !checkIsCategory(ticket)) ||
                    (isLoadingGetTicket && activeTicketId === ticket.id)
                  }>
                  <span>{getTicketName(ticket, language)}</span>
                </button>
              ))}
          </div>
        </main>
      </div>
      {scanTarget && (
        <TerminalMisScanModal
          language={language}
          target={scanTarget}
          allowsDigitalCode={allowsDigitalCode}
          allowsBarcode={allowsBarcode}
          localTest={localTest}
          isSubmitting={isConfirming}
          errorMessage={scanError}
          onCancel={() => {
            setPendingExtraData(undefined);
            closeScan();
          }}
          onSubmit={async (value) => {
            const result = await submitScan(value);
            if (result?.target.scanOnService && result.target.topicId != null) {
              const extraData = pendingExtraData;
              setPendingExtraData(undefined);
              setActiveTicketId(result.target.topicId);
              getTicket({
                topicId: result.target.topicId,
                confirmationId: result.confirmation.confirmationId,
                extraData
              });
            }
          }}
          onSkipBenefit={
            scanTarget.scanOnService && scanTarget.topicId != null
              ? () => {
                  const topicId = scanTarget.topicId;
                  const extraData = pendingExtraData;
                  setPendingExtraData(undefined);
                  closeScan();
                  if (topicId == null) return;
                  requestTicket(topicId, extraData);
                }
              : undefined
          }
        />
      )}
      {extraInputTarget && (
        <TerminalExtraDataModal
          language={language}
          description={extraInputTarget.extraInputDescription}
          settings={extraInputTarget}
          onCancel={() => {
            setPendingExtraData(undefined);
            setExtraInputTarget(null);
          }}
          onSubmit={(value) => {
            const topicId = extraInputTarget.id;
            setExtraInputTarget(null);
            if (
              !confirmation &&
              orderUnitsData?.misConfig?.benefitsTerminalMode === 'SCAN_ON_SERVICE' &&
              orderUnitsData?.misConfig?.benefitsEffective
            ) {
              setPendingExtraData(value);
              openScan({
                privilegeKind: 'BENEFIT',
                name: getTicketName(extraInputTarget, language),
                scanCard: true,
                scanOnService: true,
                topicId
              });
              return;
            }
            requestTicket(topicId, value);
          }}
        />
      )}
      {isScreensaverActive && (
        <div
          className={classes.screensaverLayer}
          onClick={dismissScreensaver}
          role="presentation"
        >
          <TerminalScreensaver
            media={screensaverMedia}
            imageSlideSeconds={screensaverImageSeconds}
          />
        </div>
      )}
    </div>
  );
};

export default PoorVisionMode;
